import { OrderStatusType, type AdminOrderListItem } from "@models/order";
import { asApiFailure } from "@utils/apiFailure";
import { formatOrderNumber } from "@utils/formatter";
import { STATUS_META } from "../_components/status";
import { MARK_PROCESSING } from "../_components/transitions";
import { transitionBlockedReason } from "./transitionBlockedReason";

// The Orders list's bulk "Mark Processed" (#473): each selected order is moved
// on its own (POST /orders/{id}/transition), one request at a time, and every
// order gets its own outcome; nothing is all-or-nothing.

export interface PlannedRow {
    order: AdminOrderListItem;
    // Why no request is sent for this order, or null when one is.
    skipReason: string | null;
}

export type MarkProcessedResult =
    | { orderId: string; orderNumber: string; outcome: "processed" }
    | {
        orderId: string;
        orderNumber: string;
        outcome: "notProcessed" | "failed";
        reason: string;
    };

// A 400 (the order is no longer Received) or a 409 (its payment is held, or
// another change landed first) both mean the row was stale. The server's own
// sentences name enum values or the order's id, so they are not shown.
const CHANGED_SINCE_LOADED =
    "It changed after the list loaded. Open the order to see its current status.";
const NOT_FOUND = "This order no longer exists.";
// A 5xx or a dropped connection: a request that never answered may still have
// landed, so this never says the order was left as it was.
const FAILED =
    "Something went wrong, and it may not have been marked. Check its status.";

const orderNumberOf = (order: AdminOrderListItem): string =>
    order.orderNumberFormatted ?? formatOrderNumber(order.orderNumber);

// Only a Received order may move to Processing (§3.5), and only once its
// payment is Captured (#442) — the detail page's rule and wording.
const skipReasonFor = (order: AdminOrderListItem): string | null =>
    order.status !== OrderStatusType.Received
        ? `It is ${STATUS_META[order.status].label}, not Received.`
        : transitionBlockedReason(MARK_PROCESSING, order.paymentStatus);

export const planMarkProcessed = (rows: AdminOrderListItem[]): PlannedRow[] =>
    rows.map((order) => ({ order, skipReason: skipReasonFor(order) }));

const outcomeOf = (
    error: unknown,
): { outcome: "notProcessed" | "failed"; reason: string } => {
    const status = asApiFailure(error)?.status;

    if (status === 400 || status === 409) {
        return { outcome: "notProcessed", reason: CHANGED_SINCE_LOADED };
    }
    if (status === 404) {
        return { outcome: "notProcessed", reason: NOT_FOUND };
    }
    return { outcome: "failed", reason: FAILED };
};

// Runs the plan in row order. `onStart` gets the 1-based position of each
// request among the rows that send one, as it is sent. Never rejects: a
// failure is that order's outcome, and the next order is still sent.
export const runMarkProcessed = async (
    plan: PlannedRow[],
    markOne: (orderId: string) => Promise<void>,
    onStart: (position: number) => void,
): Promise<MarkProcessedResult[]> => {
    const results: MarkProcessedResult[] = [];
    let position = 0;

    for (const { order, skipReason } of plan) {
        const orderId = order.id;
        const orderNumber = orderNumberOf(order);

        if (skipReason !== null) {
            results.push({
                orderId,
                orderNumber,
                outcome: "notProcessed",
                reason: skipReason,
            });
            continue;
        }

        position += 1;
        onStart(position);

        try {
            await markOne(orderId);
            results.push({ orderId, orderNumber, outcome: "processed" });
        } catch (error) {
            results.push({ orderId, orderNumber, ...outcomeOf(error) });
        }
    }

    return results;
};
